// Seasonal ordering (roadmap 14.4–14.5): the marina's rules for what each stored boat needs each
// season, and (14.5) the order they add up to. No DOM or Firebase here: Inventory ›
// Season order (seasonOrderTab.js), its Rules view (seasonalRules.js) and tests/unit/seasonal.test.mjs share it.
//
// /seasonalOrdering = {
//   paint:     { included, run, coats, factorPower, factorSail, lwlRatio, extraPct,
//                catalog: { id: { product, color, partId, unitGallons, coverage } } },
//   compound:  { service, run, products: { id: { name, partId, perFoot, unitSize, unit } } },
//   shrinkwrap:{ run, rolls: { id: { width, partId, rollFeet } } },
//   supplies:  { id: { label, run, when, partId, amount, per, unitSize } },
// }
// Paint brands are manufacturers, not vendors: a catalog row names the inventory part, and the
// part's vendors (distributors) are who it's ordered from. Coverage is never guessed: it comes
// from the maker's data sheet, and a product without it is listed as missing information.

import { parseFeet, planWrap } from './shrinkwrapCore.js';
import { anodesOf, anodeNeeds } from './anodeCore.js';

export const RUNS = [['fall', 'Fall (haul-out)'], ['spring', 'Spring (commissioning)']];
export const RUN_LABEL = Object.fromEntries(RUNS);
export const KIT_SEASONS = { fall: 'Fall', spring: 'Spring', none: 'Not seasonal' };
export const PER = [['boat', 'per boat'], ['foot', 'per foot of boat'], ['engine', 'per engine']];
export const ANY_CONTRACT = '*';

// Starting points only, all editable. Bottom area ≈ waterline length × (beam + draft) × hull
// factor (the rule of thumb paint makers publish); waterline ≈ length × lwlRatio when unknown.
export const DEFAULT_RULES = Object.freeze({
  paint: { included: true, run: 'spring', coats: 1, factorPower: 0.85, factorSail: 0.75, lwlRatio: 0.85, extraPct: 10, catalog: {} },
  compound: { service: 'Spring detailing — hull & deck wax', run: 'spring', products: {} },
  shrinkwrap: { run: 'fall', rolls: {} },
  supplies: {},
});

const num = (v, lo, hi, dflt) => {
  const n = Number(v);
  return Number.isFinite(n) && n >= lo && n <= hi ? n : dflt;
};
const run = (v, dflt) => (v === 'fall' || v === 'spring' ? v : dflt);
const str = (v, max) => String(v ?? '').trim().slice(0, max);
const obj = v => (v && typeof v === 'object' ? v : {});
const optNum = (v, lo, hi) => (v === '' || v == null || !Number.isFinite(Number(v)) || Number(v) < lo || Number(v) > hi ? null : Number(v));

/** Saved rules with every field present and in range (missing → the default). */
export function normalizeRules(saved) {
  const s = obj(saved);
  const d = DEFAULT_RULES;
  const p = obj(s.paint), c = obj(s.compound), w = obj(s.shrinkwrap);
  const rows = (o, fn) => Object.fromEntries(Object.entries(obj(o)).filter(([, r]) => r && typeof r === 'object').map(([k, r]) => [k, fn(r)]));
  return {
    paint: {
      included: p.included === undefined ? d.paint.included : p.included === true,
      run: run(p.run, d.paint.run),
      coats: num(p.coats, 1, 5, d.paint.coats),
      factorPower: num(p.factorPower, 0.1, 2, d.paint.factorPower),
      factorSail: num(p.factorSail, 0.1, 2, d.paint.factorSail),
      lwlRatio: num(p.lwlRatio, 0.5, 1, d.paint.lwlRatio),
      extraPct: num(p.extraPct, 0, 100, d.paint.extraPct),
      catalog: rows(p.catalog, r => ({
        product: str(r.product, 120), color: str(r.color, 40), partId: str(r.partId, 80) || null,
        unitGallons: optNum(r.unitGallons, 0.01, 10), coverage: optNum(r.coverage, 1, 5000),
      })),
    },
    compound: {
      service: str(c.service ?? d.compound.service, 160),
      run: run(c.run, d.compound.run),
      products: rows(c.products, r => ({
        name: str(r.name, 120), partId: str(r.partId, 80) || null,
        perFoot: optNum(r.perFoot, 0, 1000), unitSize: optNum(r.unitSize, 0.01, 100000), unit: str(r.unit, 12) || 'oz',
      })),
    },
    shrinkwrap: {
      run: run(w.run, d.shrinkwrap.run),
      rolls: rows(w.rolls, r => ({ width: optNum(r.width, 1, 100), partId: str(r.partId, 80) || null, rollFeet: optNum(r.rollFeet, 1, 5000) })),
    },
    supplies: rows(s.supplies, r => ({
      label: str(r.label, 80), run: run(r.run, 'fall'), when: str(r.when, 160) || ANY_CONTRACT, partId: str(r.partId, 80) || null,
      amount: optNum(r.amount, 0, 100000), per: ['boat', 'foot', 'engine'].includes(r.per) ? r.per : 'boat', unitSize: optNum(r.unitSize, 0.01, 100000),
    })),
  };
}

const key = s => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Each boat's (Boats/{id}) paint product + color, counted: [{ product, color, boats }]. */
export function paintInUse(boats) {
  const m = new Map();
  (boats || []).forEach(b => {
    const product = str(b?.Bottom_Paint_Product, 120);
    if (!product) return;
    const color = str(b.Bottom_Paint_Color, 40);
    const k = `${key(product)}|${key(color)}`;
    const e = m.get(k) || { product, color, boats: 0 };
    e.boats++;
    m.set(k, e);
  });
  return [...m.values()].sort((a, b) => a.product.localeCompare(b.product) || a.color.localeCompare(b.color));
}

/** The products in boats' compound / polish / wax recipes ("A → B → C"), counted. */
export function compoundInUse(boats) {
  const m = new Map();
  (boats || []).forEach(b => {
    String(b?.Detailing_Compound || '').split(/→|->|\+|;/).map(x => x.replace(/\(.*?\)/g, '').trim()).filter(Boolean).forEach(name => {
      const k = key(name);
      const e = m.get(k) || { name: str(name, 120), boats: 0 };
      e.boats++;
      m.set(k, e);
    });
  });
  return [...m.values()].sort((a, b) => b.boats - a.boats || a.name.localeCompare(b.name));
}

/** The catalog row for a boat's paint: product + color first, then the product with no color. */
export function catalogRowFor(catalog, product, color) {
  const rows = Object.entries(obj(catalog));
  const p = key(product), c = key(color);
  if (!p) return null;
  const hit = rows.find(([, r]) => key(r.product) === p && key(r.color) === c) || rows.find(([, r]) => key(r.product) === p && !key(r.color));
  return hit ? { id: hit[0], ...hit[1] } : null;
}

/** Paint products boats use that the catalog doesn't cover yet (one per product; colors share a row). */
export function uncataloguedPaint(boats, catalog) {
  const seen = new Set();
  return paintInUse(boats).filter(u => {
    if (catalogRowFor(catalog, u.product, u.color)) return false;
    const k = key(u.product);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  }).map(u => ({ product: u.product, boats: paintInUse(boats).filter(x => key(x.product) === key(u.product)).reduce((t, x) => t + x.boats, 0) }));
}

const words = s => key(s).replace(/[^a-z0-9 ]/g, ' ').split(' ').filter(w => w.length > 1);

/**
 * The inventory part a product name most likely is: every word of the name in the part's
 * description / maker / number, fewest extra words first. Only a suggestion.
 */
export function suggestPart(name, parts) {
  const want = words(name);
  if (!want.length) return null;
  let best = null;
  (parts || []).forEach(p => {
    if (!p || !p.id) return;
    const have = new Set(words(`${p.Description || ''} ${p.Manufacturer || ''} ${p.Part_Number || ''}`));
    if (!want.every(w => have.has(w))) return;
    const extra = have.size - want.length;
    if (!best || extra < best.extra) best = { part: p, extra };
  });
  return best ? best.part : null;
}

/** Storage-contract services (names) for the "when" pickers, plus "every storage contract". */
export function serviceChoices(catalog) {
  return [[ANY_CONTRACT, 'Every storage contract'], ...(catalog || []).map(s => [s.service, s.service])];
}

// ---------- the season order (14.5) ----------

const COUNTED_STATUS = s => !['draft', 'cancelled', 'canceled', 'declined', 'void'].includes(String(s || '').toLowerCase());
const SAIL = /sail|sloop|ketch|yawl|cutter|schooner/i;
const normPn = pn => String(pn || '').toUpperCase().replace(/[\s-]/g, '');
const round2 = n => Math.round(n * 100) / 100;
const fmtAmt = n => (Number.isInteger(round2(n)) ? String(round2(n)) : round2(n).toFixed(2));
const customerName = c => [c?.First_Name, c?.Last_Name].map(x => String(x || '').trim()).filter(Boolean).join(' ') || String(c?.Customer_Name || '').trim();

/** Storage seasons the contracts use ('2026-27'…), newest first. */
export function contractSeasons(customers) {
  const set = new Set();
  Object.values(obj(customers)).forEach(c => Object.values(obj(c?.Boats)).forEach(b => { const s = b?.Storage_Contract?.season; if (s) set.add(String(s)); }));
  return [...set].sort().reverse();
}

/** "1× shaft collar anode (1¼″) · 2× trim tab anode · aluminum" → [{ qty: 1, name: 'shaft collar anode (1¼″) (aluminum)' }, …] */
export function parseAnodes(text) {
  const parts = String(text || '').split(/·|;|\n/).map(x => x.trim()).filter(Boolean);
  const material = parts.find(x => /^(zinc|aluminum|aluminium|magnesium)$/i.test(x));
  return parts.map(x => x.match(/^(\d+)\s*[×x]\s*(.+)$/i)).filter(Boolean)
    .map(m => ({ qty: Number(m[1]), name: `${m[2].trim()}${material ? ` (${material.toLowerCase()})` : ''}` }));
}

/**
 * One boat's bottom paint for a season: { area (sq ft), gallons }, or null when its size or the catalog
 * row's coverage isn't known. R: normalizeRules(); row: catalogRowFor() for the boat's paint.
 */
export function paintNeed(b, R, row) {
  const L = parseFeet(b?.Boat_Length), B = parseFeet(b?.Boat_Beam), D = parseFeet(b?.Boat_Draft);
  if (!L || !B || !D || !row?.coverage) return null;
  const sail = SAIL.test(String(b.Boat_Type || ''));
  const lwl = parseFeet(b.Boat_LWL) || L * R.paint.lwlRatio;
  const area = lwl * (B + D) * (sail ? R.paint.factorSail : R.paint.factorPower);
  return { area, gallons: area * R.paint.coats / row.coverage * (1 + R.paint.extraPct / 100) };
}

/**
 * What the boats with a storage contract for `season` need for one run ('fall' | 'spring'):
 *   lines:   [{ key, partId, part, partNumber, description, vendorName, qty, detail, boats: [{ boatId, customerId, boat, customer, amount, why }] }]
 *            qty is in the part's own units (cans, bottles, rolls, each); typed items (no inventory part) have partId null.
 *   missing: [{ reason, fix, boats: [{ boatId, customerId, boat, customer }] }]  what stopped a need being counted
 *   boats:   how many boats were counted.
 * serviceCatalog is the storage-contract service list (STORAGE_SERVICE_CATALOG), for shrinkwrap.
 */
export function seasonOrder({ run, season, rules, customers, parts, serviceCatalog }) {
  const R = normalizeRules(rules);
  const byId = new Map((parts || []).filter(p => p && p.id).map(p => [p.id, p]));
  const byPn = new Map((parts || []).filter(p => p && p.Part_Number).map(p => [normPn(p.Part_Number), p]));
  const wrapServices = new Set((serviceCatalog || []).filter(s => s.season === 'shrinkwrap' && /^shrink ?wrap & frame/i.test(s.service)).map(s => s.service));
  const groups = new Map(); // `${key}|${unitSize}` → { key, amount, unitSize, unit, boats }
  const missing = new Map(); // reason → { reason, fix, boats }
  let counted = 0;

  const miss = (reason, fix, who) => {
    if (!missing.has(reason)) missing.set(reason, { reason, fix, boats: [] });
    const m = missing.get(reason);
    if (!m.boats.some(b => b.boatId === who.boatId && b.note === who.note)) m.boats.push(who);
  };
  const add = (lineKey, base, amount, unitSize, unit, who, why) => {
    const gk = `${lineKey}|${unitSize}`;
    if (!groups.has(gk)) groups.set(gk, { key: lineKey, ...base, amount: 0, unitSize, unit, boats: [] });
    const g = groups.get(gk);
    g.amount += amount;
    g.boats.push({ ...who, amount: `${fmtAmt(amount)}${unit ? ` ${unit}` : ''}`, why });
  };
  const partLine = p => ({ partId: p.id, part: p, partNumber: p.Part_Number || '', description: p.Description || '', vendorName: (Array.isArray(p.Vendor_List) && p.Vendor_List[0]) || '' });
  const typedLine = d => ({ partId: null, part: null, partNumber: '', description: d, vendorName: '' });

  Object.entries(obj(customers)).forEach(([customerId, c]) => {
    if (!c || typeof c !== 'object') return;
    Object.entries(obj(c.Boats)).forEach(([boatId, b]) => {
      if (!b || typeof b !== 'object') return;
      const sc = b.Storage_Contract;
      const who = { boatId, customerId, boat: b.Boat_Name || 'Boat', customer: customerName(c) };
      if (!sc || !COUNTED_STATUS(sc.status)) return;
      if (!sc.season) { miss('Storage contract has no season', 'Open the contract and save it for a season.', who); return; }
      if (String(sc.season) !== String(season)) return;
      counted++;
      const sel = obj(sc.selections);
      const has = name => name === ANY_CONTRACT || !!sel[name];
      const L = parseFeet(b.Boat_Length), B = parseFeet(b.Boat_Beam), D = parseFeet(b.Boat_Draft);

      // Parts Kits tagged for this run.
      const seasons = obj(b.Kit_Seasons);
      Object.entries(obj(b.Parts_Kits)).forEach(([kitName, kit]) => {
        const tag = seasons[kitName];
        if (!tag) { miss('Parts kit has no season', 'Customers › boat › Parts Kits: set Fall, Spring or Not seasonal.', { ...who, note: kitName }); return; }
        if (tag !== run) return;
        Object.entries(obj(kit)).forEach(([pn, info]) => {
          const qty = Number(info?.qty) || 1;
          const p = (info?.id && byId.get(info.id)) || byPn.get(normPn(pn));
          if (p) add(`p:${p.id}`, partLine(p), qty, 1, '', who, `Kit: ${kitName}`);
          else { const d = String(info?.description || pn).trim(); add(`t:${d.toLowerCase()}`, typedLine(d), qty, 1, '', who, `Kit: ${kitName}`); }
        });
      });

      // Bottom paint (and anodes, done with it).
      if (R.paint.included && R.paint.run === run) {
        const product = String(b.Bottom_Paint_Product || '').trim();
        const row = product ? catalogRowFor(R.paint.catalog, product, b.Bottom_Paint_Color) : null;
        const part = row?.partId ? byId.get(row.partId) : null;
        if (!product) miss('No bottom paint product on the boat', 'Customers › boat › Bottom Paint Product.', who);
        else if (!row) miss(`“${product}” isn’t in the paint catalog`, 'Season order › Rules › Paint catalog.', who);
        else if (!part) miss(`“${row.product}” has no inventory part`, 'Season order › Rules › Paint catalog.', who);
        else if (!row.coverage || !row.unitGallons) miss(`“${row.product}” needs its coverage and can size`, 'Season order › Rules › Paint catalog (from the maker’s data sheet).', who);
        else if (!L || !B || !D) miss('Length, beam or draft missing (needed for paint)', 'Customers › boat: Length, Beam, Draft.', who);
        else {
          const { area, gallons: gal } = paintNeed(b, R, row);
          add(`p:${part.id}`, partLine(part), gal, row.unitGallons, 'gal', who,
            `Bottom paint: ${product}${b.Bottom_Paint_Color ? ` ${b.Bottom_Paint_Color}` : ''}, ${Math.round(area)} sq ft × ${R.paint.coats} coat${R.paint.coats === 1 ? '' : 's'}`);
        }
        if (/^(marina|obmc)$/i.test(String(sc.zincChoice || ''))) {
          // The boat's anode lines (read from Anode_List until it has some), by inventory part where one is linked.
          anodeNeeds(anodesOf(b), id => byId.get(id) || null).forEach((n) => {
            const part = n.partId ? byId.get(n.partId) : null;
            if (part) add(`p:${part.id}`, partLine(part), n.qty, 1, '', who, 'Anodes (the marina replaces them)');
            else add(`t:${n.label.toLowerCase()}`, typedLine(n.label), n.qty, 1, '', who, 'Anodes (the marina replaces them)');
          });
        }
      }

      // Compound and wax, from the boat's recipe.
      if (R.compound.run === run && R.compound.service && has(R.compound.service)) {
        const names = String(b.Detailing_Compound || '').split(/→|->|\+|;/).map(x => x.replace(/\(.*?\)/g, '').trim()).filter(Boolean);
        if (!names.length) miss('No detailing compound recipe on the boat', 'Customers › boat › Detailing Compound.', who);
        else if (!L) miss('No length (needed for compound)', 'Customers › boat › Length.', who);
        names.forEach(name => {
          const row = Object.values(R.compound.products).find(r => key(r.name) === key(name));
          const part = row?.partId ? byId.get(row.partId) : null;
          if (!row) { miss(`“${name}” isn’t in the compound list`, 'Season order › Rules › Compound and wax.', who); return; }
          if (!part || row.perFoot == null || !row.unitSize) { miss(`“${row.name}” needs a part, amount per foot and size`, 'Season order › Rules › Compound and wax.', who); return; }
          if (L) add(`p:${part.id}`, partLine(part), row.perFoot * L, row.unitSize, row.unit, who, `Detailing: ${row.name}`);
        });
      }

      // Shrinkwrap.
      if (R.shrinkwrap.run === run && Object.keys(sel).some(s => sel[s] && wrapServices.has(s))) {
        const w = planWrap(b);
        const roll = w && Object.values(R.shrinkwrap.rolls).find(r => r.width === w.width);
        const part = roll?.partId ? byId.get(roll.partId) : null;
        if (!w) miss('No length or beam (needed for the shrinkwrap size)', 'Customers › boat: Length, Beam.', who);
        else if (!roll || !part || !roll.rollFeet) miss(`No ${w.width} ft shrinkwrap roll set up`, 'Season order › Rules › Shrinkwrap.', who);
        else add(`p:${part.id}`, partLine(part), w.length, roll.rollFeet, 'ft', who, `Shrinkwrap ${w.width} × ${w.length} ft (${w.source === 'recorded' ? 'size used last time' : 'estimated'})`);
      }

      // Other supplies.
      Object.values(R.supplies).forEach(sup => {
        if (sup.run !== run || !has(sup.when)) return;
        const part = sup.partId ? byId.get(sup.partId) : null;
        if (!part || sup.amount == null || !sup.unitSize) { miss(`“${sup.label || 'Supply'}” needs a part, amount and size`, 'Season order › Rules › Other supplies.', who); return; }
        let n = 1;
        if (sup.per === 'foot') n = L;
        if (sup.per === 'engine') n = parseInt(b.Number_of_Engines, 10) || Object.keys(obj(b.Engines)).length || null;
        if (!n) { miss(sup.per === 'foot' ? 'No length (needed for supplies)' : 'Number of engines unknown', 'Customers › boat.', who); return; }
        add(`p:${part.id}`, partLine(part), sup.amount * n, sup.unitSize, '', who, `${sup.label}${sup.per === 'boat' ? '' : ` (${fmtAmt(sup.amount)} per ${sup.per})`}`);
      });
    });
  });

  // Units: each (part, size) group rounds up on its own; a part's groups add up.
  const lines = new Map();
  groups.forEach(g => {
    const units = Math.ceil(round2(g.amount / g.unitSize));
    if (!lines.has(g.key)) {
      const { amount, unitSize, unit, boats, ...base } = g;
      lines.set(g.key, { ...base, qty: 0, details: [], boats: [] });
    }
    const l = lines.get(g.key);
    l.qty += units;
    l.details.push(g.unitSize === 1 && !g.unit ? fmtAmt(g.amount) : `${fmtAmt(g.amount)} ${g.unit} ÷ ${fmtAmt(g.unitSize)}${g.unit ? ` ${g.unit}` : ''}`);
    l.boats.push(...g.boats);
  });
  return {
    lines: [...lines.values()].map(({ details, ...l }) => ({ ...l, detail: details.join(' + ') }))
      // Vendors A–Z, then parts with no vendor, then items not in inventory.
      .sort((a, b) => (!a.partId - !b.partId) || (!a.vendorName - !b.vendorName) || String(a.vendorName).localeCompare(String(b.vendorName))
        || (a.partNumber || a.description).localeCompare(b.partNumber || b.description)),
    missing: [...missing.values()].sort((a, b) => b.boats.length - a.boats.length),
    boats: counted,
  };
}
